import React, { useState } from 'react';

import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductHeader = ({ product, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState(product);

  const handleSave = () => {
    onEdit(editedProduct);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedProduct(product);
    setIsEditing(false);
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case 'In Stock': return 'text-success bg-success/10';
      case 'Low Stock': return 'text-warning bg-warning/10';
      case 'Out of Stock': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-card">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Product Image */}
        <div className="flex-shrink-0">
          <div className="w-48 h-48 bg-muted rounded-lg overflow-hidden">
            <Image
              src={product?.image}
              alt={product?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" iconName="Camera" iconPosition="left">
              Update Photo
            </Button>
            <Button variant="ghost" size="sm" iconName="Eye">
              View All
            </Button>
          </div>
        </div>

        {/* Product Information */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-4">
            <div>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProduct?.name}
                  onChange={(e) => setEditedProduct({...editedProduct, name: e?.target?.value})}
                  className="text-2xl font-semibold bg-input border border-border rounded px-3 py-1 mb-2"
                />
              ) : (
                <h1 className="text-2xl font-semibold text-foreground mb-2">{product?.name}</h1>
              )}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>SKU: {product?.sku}</span>
                <span>•</span>
                <span>Category: {product?.category}</span>
                <span>•</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStockStatusColor(product?.stockStatus)}`}>
                  {product?.stockStatus}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button variant="default" size="sm" onClick={handleSave}>
                    Save
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCancel}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" iconName="Edit" onClick={() => setIsEditing(true)}>
                    Edit
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
            {isEditing ? (
              <textarea
                value={editedProduct?.description}
                onChange={(e) => setEditedProduct({...editedProduct, description: e?.target?.value})}
                className="w-full bg-input border border-border rounded px-3 py-2 text-sm resize-none"
                rows={3}
              />
            ) : (
              <p className="text-sm text-muted-foreground leading-relaxed">
                {product?.description}
              </p>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Total Stock</div>
              <div className="text-lg font-semibold text-foreground">{product?.totalStock}</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Unit Price</div>
              <div className="text-lg font-semibold text-foreground">${product?.unitPrice}</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Reorder Point</div>
              <div className="text-lg font-semibold text-foreground">{product?.reorderPoint}</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Days of Supply</div>
              <div className="text-lg font-semibold text-foreground">{product?.daysOfSupply}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductHeader;